import Utils from "Utils/utils";
import InboxUtils from "Utils/InboxUtils";
import PaywallUtils from "Utils/PaywallUtils";
import DriverMaster from "Components/driver/Driver";
import { toasterState } from "Components/common/toaster";
import {
  AUTH_CLIENT_ID,
  AUTH_REDIRECT_URL,
  AUTH_SCOPES,
  AUTH_SCOPES_CALENDAR,
  OUTLOOK_CLIENT_ID,
  OUTLOOK_REDIRECT_URL,
} from "Static/constant";
import userDetail from "Stores/userDetail";
import connectedInboxStore from "../ConnectInbox/store";
import { gmailMessagePopupState, showAlreadyConnectedPopup } from "../ConnectInbox/GmailMessagePopup";
import reconnectSequencePopupState from "../ConnectInbox/ReconnectSequencePopupState";

const connectedEmailUtils = {};

const handleGoogleOauthCbk = async (cbkCode = "", inboxId = "", connectedInbox = {}) => {
  const { noOfInboxesUsed = 0 } = userDetail?.userFeatureFlag || {};

  switch (cbkCode) {
    case "200":
      connectedInboxStore.setLoading(true);
      userDetail.setUserFeatureFlag({
        ...userDetail.userFeatureFlag,
        noOfInboxesUsed: noOfInboxesUsed + 1,
      });
      await connectedInboxStore.getAccountDetails(true);
      window.postMessage({ type: "CONNECT_INBOX_SUCCESS" }, "*");
      Utils.getOnboardingData();
      Utils.trackOnboardingMixpanel("account_connected");
      if (inboxId && Object.keys(connectedInbox).length > 0) {
        await reconnectSequencePopupState.getAllCampaignSequences(inboxId, connectedInbox);
      }
      break;
    case "400":
      gmailMessagePopupState.setPopupValues({
        title: "Insufficient Permission",
        subTitle: `Unable to connect your Google account with Salesgear due to insufficient permissions.
      Please retry connecting by enabling all the required permissions on the consent screen 
      <br>(Please refer to the video below)`,
      });
      gmailMessagePopupState.setShowPopup(true);
      break;
    case "409":
      showAlreadyConnectedPopup("Google");
      break;
    case "412":
      gmailMessagePopupState.setPopupValues({
        title: "Connection Failed",
        subTitle: `Please connect the same email account`,
      });
      gmailMessagePopupState.setShowPopup(true);
      break;
    case "413":
      toasterState.setToastMsg(
        `Oops! You cannot connect personal email addresses.`,
        "fail"
      );
      break;
    case "500":
    default:
      toasterState.setToastMsg(
        "Something went wrong. Please try again later",
        "fail"
      );
  }
};

const closeGmailMessagePopup = () => {
  gmailMessagePopupState.setPopupValues({});
  gmailMessagePopupState.setShowPopup(false);
};

connectedEmailUtils.openGmailConnectPopup = () => {
  if (InboxUtils.connectInboxAllowed()) {
    DriverMaster.killDriver();
    gmailMessagePopupState.setConnectBtnAction(
      connectedEmailUtils.openGoogleAuth
    );
    gmailMessagePopupState.setPopupValues({
      title: `Connect your Google account with Salesgear`,
      subTitle: "",
    });
    gmailMessagePopupState.setShowPopup(true);
  } else {
    upgradeMulitpleInbox();
  }
};

connectedEmailUtils.openGoogleAuth = async () => {
  const { calendarEnabled = false } = userDetail?.userInfo || {};
  const { calendarIntegrationEnabled = false } =
    userDetail?.userFeatureFlag || {};

  closeGmailMessagePopup();
  let gmailAuthScope =
    calendarEnabled && calendarIntegrationEnabled
      ? AUTH_SCOPES_CALENDAR
      : AUTH_SCOPES;
  const authUrl =
    "https://accounts.google.com/o/oauth2/auth?client_id=" +
    AUTH_CLIENT_ID +
    "&scope=" +
    gmailAuthScope +
    "&access_type=offline&response_type=code&redirect_uri=" +
    AUTH_REDIRECT_URL +
    "&prompt=select_account" +
    "&hd=*";
  // The app runs inside an iframe (Shell.js); after the popup round-trips
  // through Google, Chrome severs `window.opener`, so the popup can't close
  // itself and plain postMessage gets dropped. openConnectPopup closes the
  // popup from here and receives the result over the multi-channel bridge.
  const { openConnectPopup } = require("Pages/Dashboard/connectApps/oauthPopupBridge");
  openConnectPopup({
    url: authUrl,
    types: ["GOOGLE_CONNECT_INBOX"],
    onResult: (data) =>
      handleGoogleOauthCbk(data?.code?.toString(), data?.inboxId, data?.connectedInbox)
  });
};

connectedEmailUtils.microsoftAuth = async () => {
  const { noOfInboxesUsed = 0 } = userDetail?.userFeatureFlag || {};

  if (InboxUtils.connectInboxAllowed()) {
    DriverMaster.killDriver();
    const authUrl =
      "https://login.microsoftonline.com/common/oauth2/v2.0/authorize?" +
      "client_id=" +
      OUTLOOK_CLIENT_ID +
      "&response_type=code" +
      "&redirect_uri=" +
      OUTLOOK_REDIRECT_URL +
      "&response_mode=query" +
      "&scope=offline_access openid Mail.ReadWrite Contacts.Read email Mail.Read Mail.Send profile User.Read Calendars.ReadWrite Calendars.ReadBasic Calendars.Read" +
      "&state=12345" +
      "&prompt=select_account";
    // Popup can't self-close inside the Shell iframe (COOP severs opener);
    // openConnectPopup closes it from here and delivers the result.
    const { openConnectPopup } = require("Pages/Dashboard/connectApps/oauthPopupBridge");
    openConnectPopup({
      url: authUrl,
      types: ["MICROSOFT_CONNECT_INBOX"],
      onResult: async (msg = {}) => {
        const { code = "", inboxId = "", connectedInbox = {} } = msg;
        if (code === "200" || code === 200) {
          userDetail.setUserFeatureFlag({
            ...userDetail.userFeatureFlag,
            noOfInboxesUsed: noOfInboxesUsed + 1,
          });
          connectedInboxStore.setLoading(true);
          await connectedInboxStore.getAccountDetails(true);
          Utils.getOnboardingData();
          Utils.trackOnboardingMixpanel("account_connected");
          if (inboxId && Object.keys(connectedInbox).length > 0) {
            await reconnectSequencePopupState.getAllCampaignSequences(inboxId, connectedInbox);
          }
        } else if (code === "409" || code === 409) {
          showAlreadyConnectedPopup("Microsoft");
        } else {
          toasterState.setToastMsg(
            "Something went wrong. Please try again later",
            "fail"
          );
        }
      }
    });
  } else {
    upgradeMulitpleInbox();
  }
};

const upgradeMulitpleInbox = () => {
  const { planName = "", inFreePlan = false } =
    userDetail?.userFeatureFlag || {};
  let tempPlanName = planName?.toLowerCase();
  let inboxModel =
    inFreePlan || ["starter", "basic"].includes(tempPlanName)
      ? "multipleInbox"
      : ["pro", "unlimited", "growth"].includes(tempPlanName)
        ? "multipleInboxCustom"
        : "";

  inboxModel &&
    PaywallUtils.upgradeUtilsFunction(inboxModel, false, planName, inFreePlan);
};

export { connectedEmailUtils };
